import type { ToolPart } from '../../types';
import { useLayoutStore } from "../../layout/LayoutProvider";

/**
 * Inline renderer for `render_infographic` tool calls. Compact clickable
 * plate showing the infographic title — clicking opens the infographic
 * view in the main slot pinned to THIS call's spec.
 *
 * Auto-open of the latest infographic is centralized in ChatPage via
 * `state.infographic` versioning; this card only handles user-initiated
 * navigation back to historical infographics after newer ones have
 * overwritten the live slot.
 */

interface Props {
  part: ToolPart;
}

export function InfographicCard({ part }: Props) {
  const { mainView, openMain } = useLayoutStore();

  const isActive = part.state.status === 'running';
  const isError = part.state.status === 'error';
  const isOpen = mainView?.id === 'infographic';
  const dotClass = isError
    ? 'tool-dot-error'
    : isActive
      ? 'tool-dot-active'
      : 'tool-dot-done';

  const input = part.state.input;
  const title = (input?.title as string | undefined) || 'инфографика';
  const blocks = input?.blocks as unknown[] | undefined;
  const subtitle = input?.subtitle as string | undefined;

  const label = isActive
    ? `Строю: ${title}`
    : isError
      ? `Не удалось построить: ${title}`
      : `Инфографика: ${title}`;

  const handleClick = () => {
    if (blocks && Array.isArray(blocks) && blocks.length > 0) {
      openMain('infographic', { callTitle: title, callSubtitle: subtitle, callBlocks: blocks });
    } else if (!isOpen) {
      openMain('infographic');
    }
  };

  const hint = isOpen ? 'Открыта' : 'Открыть';

  return (
    <div
      className={`tool-step tool-${part.state.status} canvas-card${isOpen ? ' canvas-card--open' : ''}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') handleClick(); }}
      title={isOpen ? 'Инфографика открыта' : 'Открыть инфографику'}
    >
      <div className="tool-step-rail">
        <div className={`tool-step-dot ${dotClass}`} />
      </div>
      <div className="tool-step-body canvas-card-body">
        <div className={`tool-step-label ${isActive ? 'tool-active' : ''}`}>
          {label}
        </div>
        {isError && part.state.error && (
          <div className="tool-step-content">
            <pre className="tool-error">{part.state.error}</pre>
          </div>
        )}
      </div>
      {!isError && (
        <div className="canvas-card-cta" aria-hidden>
          <span className="canvas-card-cta-text">{hint}</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14" />
            <path d="M12 5l7 7-7 7" />
          </svg>
        </div>
      )}
    </div>
  );
}
